import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { testimonials } from '@/mocks/homeData';

export default function Testimonials() {
  const [active, setActive] = useState(0);

  return (
    <section className="relative py-20 md:py-28 bg-primary-900 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-500/3 rounded-full blur-[150px]"></div>
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-20"
        >
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-white/50 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Hear from the businesses that rely on BEKAALI for their trade and procurement operations.
          </p>
        </motion.div>

        {/* Testimonials Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          {/* Active testimonial */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-8 md:p-12 mb-8 backdrop-blur-sm"
            >
              <div className="text-accent-500/30 text-6xl font-serif mb-6 leading-none">&ldquo;</div>

              <blockquote className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 font-light">
                {testimonials[active].content}
              </blockquote>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent-500/20 flex items-center justify-center text-accent-400 font-heading font-bold text-lg">
                  {testimonials[active].name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-heading font-semibold text-white">{testimonials[active].name}</div>
                  <div className="text-xs text-white/50">{testimonials[active].role}</div>
                  <div className="text-xs text-accent-400/60 mt-0.5">{testimonials[active].company}</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {Array.from({ length: testimonials[active].rating }).map((_, i) => (
                    <motion.i
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 + i * 0.08 }}
                      className="ri-star-fill text-accent-400 text-sm"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Dots */}
          <div className="flex items-center justify-center gap-3">
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setActive(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  index === active
                    ? 'w-8 h-2 bg-accent-500'
                    : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Testimonial ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}